<template>
  <AdminLayout>
    <v-row align="stretch" class="mb-16 gutters">
      <v-col cols="12" class="mx-xs-auto d-flex flex-column height">
        <v-text-field
          v-model="search"
          label="Benutzer suchen..."
          append-inner-icon="mdi-magnify"
          variant="solo"
          clearable
          class="search-field"
        ></v-text-field>
        <v-skeleton-loader type="table" class="flex">
          <v-data-table
            :headers="headers"
            :sort-by="['created']"
            :footer-props="{
              'items-per-page-all-text': 'Alle',
              'items-per-page-text': 'Benutzer pro Seite',
            }"
            :items="filteredUsers"
            class="bg-accent elevation-1"
            :loading="loading"
            loading-text="Daten werden geladen..."
            no-data-text="Keine Daten vorhanden"
            fixed-header
          >
            <template v-slot:item.groups="{ item }">
              <div
                class="d-flex align-center"
                style="max-width: 180px; overflow: hidden"
              >
                <template v-for="(groupId, i) in item.groups.slice(0, 2)">
                  <template v-if="getUserGroupById(groupId)">
                    <v-chip
                      size="small"
                      class="mr-1 text-black text-truncate"
                      variant="flat"
                      color="secondary"
                      :key="i"
                      style="max-width: 120px"
                      :title="getUserGroupById(groupId)?.name"
                    >
                      <span class="text-truncate">
                        {{ getUserGroupById(groupId)?.name }}
                      </span>
                    </v-chip>
                  </template>
                </template>
                <span v-if="item.groups.length > 2" class="text-caption ml-1">
                  +{{ item.groups.length - 2 }}
                </span>
              </div>
            </template>
            <template v-slot:item.isVerified="{ item }">
              <v-checkbox
                v-model="item.isVerified"
                hide-details
                color="primary"
                readonly
              ></v-checkbox>
            </template>
            <template v-slot:item.isSuspended="{ item }">
              <v-checkbox
                v-model="item.isSuspended"
                hide-details
                color="primary"
                readonly
              ></v-checkbox>
            </template>
            <template v-slot:item.roles="{ item }">
              <v-chip
                v-for="(role, index) in item.roles"
                class="ma-2"
                color="secondary"
                :key="index"
              >
                {{ getRoleName(role) }}
              </v-chip>
            </template>
            <template v-slot:item.created="{ item }">
              <span>{{
                Intl.DateTimeFormat("de-DE", {
                  dateStyle: "short",
                  timeStyle: "short",
                }).format(new Date(item.created))
              }}</span>
            </template>
            <template v-slot:item.controls="{ item }">
              <span v-if="item.id !== 'super-admin'">
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn icon variant="text" v-bind="props" size="small">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      link
                      @click="onOpenEditUser(item.id)"
                      :disabled="!UserPermissionService.allowUpdate(item)"
                      prepend-icon="mdi-pencil"
                    >
                      <v-list-item-title>Benutzer bearbeiten</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      link
                      @click="onOpenDeleteDialog(item.id)"
                      :disabled="!UserPermissionService.allowDelete(item)"
                      prepend-icon="mdi-delete"
                    >
                      <v-list-item-title>Benutzer löschen</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </span>
            </template>
          </v-data-table>
        </v-skeleton-loader>
      </v-col>
    </v-row>
    <UserEdit
      :user="selectedUser"
      :roles="api.roles"
      :open="openEditDialog"
      @close="onCloseDialog"
    />
    <UserDeleteConformationDialog
      :toDelete="selectedUser"
      :open="openDeleteDialog"
      @close="onCloseDeleteDialog"
    />
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import { mapActions, mapGetters } from "vuex";
import ApiUsersService from "@/services/api/ApiUsersService";
import UserEdit from "@/components/User/UserEdit";
import UserDeleteConformationDialog from "@/components/User/userDeleteConformationDialog";
import UserPermissionService from "@/services/permissions/UserPermissionService";
import ApiUserGroupService from "../../services/api/ApiUserGroupService";

export default {
  components: {
    UserDeleteConformationDialog,
    AdminLayout,
    UserEdit,
  },
  data() {
    return {
      api: {
        users: [],
        userGroups: [],
      },
      headers: [
        { title: "Vorname", value: "firstName", sortable: true },
        { title: "Nachname", value: "lastName", sortable: true },
        { title: "E-Mail Adresse", value: "id", sortable: true },
        {
          title: "Benutzergruppe",
          align: "start",
          value: "groups",
        },
        { title: "Verifiziert", value: "isVerified", sortable: true },
        { title: "Suspendiert", value: "isSuspended", sortable: true },
        { title: "Beigetreten", value: "created", sortable: true },
        { title: "", value: "controls", sortable: false },
      ],
      search: "",
      openEditDialog: false,
      openDeleteDialog: false,
      selectedUser: {},
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
    }),
    UserPermissionService() {
      return UserPermissionService;
    },
    filteredUsers() {
      if (!this.search) return this.api.users;

      const s = this.search.toLowerCase();

      return this.api.users.filter((user) => {
        const roles = user.roles
          ?.map((r) => this.getRoleName(r)?.toLowerCase())
          .join(" ");

        const groups = user.groups
          ?.map((g) => this.getUserGroupById(g)?.name?.toLowerCase())
          .join(" ");

        return (
          user.firstName?.toLowerCase().includes(s) ||
          user.lastName?.toLowerCase().includes(s) ||
          user.id?.toLowerCase().includes(s) ||
          roles?.includes(s) ||
          groups?.includes(s)
        );
      });
    },
  },
  methods: {
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
    }),
    fetchUsers() {
      this.startLoading("fetch-users");

      ApiUsersService.getUsers()
        .then((response) => {
          this.api.users = response;
        })
        .finally(() => {
          this.stopLoading("fetch-users");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async fetchUserGroups() {
      await ApiUserGroupService.getInstanceLevelUserGroups().then((result) => {
        this.api.userGroups = result;
      });
    },
    onOpenEditUser(userId) {
      this.selectedUser = Object.assign(
        {},
        this.api.users.find((user) => user.id === userId)
      );
      this.openEditDialog = true;
    },
    onOpenDeleteDialog(userId) {
      this.selectedUser = Object.assign(
        {},
        this.api.users.find((user) => user.id === userId)
      );
      this.openDeleteDialog = true;
    },
    onCloseDialog() {
      this.fetchUsers();
      this.openEditDialog = false;
    },
    onCloseDeleteDialog() {
      this.fetchUsers();
      this.openDeleteDialog = false;
    },
    getRoleName(roleId) {
      return this.api.roles.find((role) => role.id === roleId)?.name;
    },
    getUserGroupById(groupId) {
      const userGroup = this.api.userGroups.find(
        (group) => group.id === groupId
      );
      return userGroup;
    },
    customFilter(value, search) {
      if (value?.toString().toLowerCase().includes(search.toLowerCase())) {
        return true;
      } else if (typeof value === "object" && value?.length > 0) {
        const roleIds = this.api.roles?.map((role) => role.id);
        for (const key in value) {
          if (roleIds?.includes(value[key])) {
            if (
              this.getRoleName(value[key])
                .toLowerCase()
                ?.includes(search.toLowerCase())
            ) {
              return true;
            }
          }
        }
      }
    },
  },
  created() {
    this.fetchUserGroups();
    this.fetchUsers();
  },
};
</script>

<style scoped>
.search-field {
  border-radius: 15px;
}
</style>
